/**
 * "Compare your view" (screens §P6 Beat 3; `decideSlots.croCompare`): the deterministic CRO memo for this draft, opened after the CRO's
 * first pass. `croMemoCompare` answers 409 CRO_FIRST_PASS_REQUIRED before it, shown as the plain sentence. Display only.
 */
import { useState, type ComponentType } from "react";
import type { ErrorBody, TeamState } from "@bsim/contracts";
import type { CroCompareProps } from "../../slotTypes";
import { errorSentence } from "../../../errors";
import { GxCroMemoList } from "../../../theme/CroMemoList";
import { GxBadge } from "../../../theme/primitives";

export const CroCompare: ComponentType<CroCompareProps> = ({ team, croMemoCompare, onMemo }) => {
  const round = team.round;
  const [memo, setMemo] = useState<TeamState["croMemo"]>(null);
  const [error, setError] = useState<ErrorBody["error"] | null>(null);
  const [pending, setPending] = useState(false);
  if (round === null) return null;
  const notes = memo?.memo.items.filter((i) => i.severity === "info").length ?? 0;

  const open = async () => {
    setPending(true);
    const r = await croMemoCompare({ teamId: team.team.id, roundId: round.id }, undefined);
    setPending(false);
    if (r.ok) { setMemo(r.value); onMemo?.(r.value); setError(null); } else setError(r.error);
  };

  return (
    <section className="gx-card gx-sign-side-card" data-testid="decide.compare">
      <div className="gx-eyebrow">Compare your view</div>
      {memo === null ? (
        <>
          <button type="button" className="gx-btn gx-btn--secondary" data-testid="decide.compare.open" disabled={pending} onClick={() => void open()}>Compare with the risk officer's memo →</button>
          {error === null ? null : <p className="gx-small gx-muted" role="status" data-testid="decide.compare.error">{errorSentence(error)}</p>}
        </>
      ) : (
        <div data-testid="decide.compare.memo">
          {/* The memo's notes are the ones the banner above the preview already prints; they are counted here, never listed a second time. */}
          <p className="gx-small gx-muted">The memo for v{memo.draftVersion} from the plan alone. What did you find that it did not?{" "}
            <GxBadge tone="neutral">{memo.memo.items.length} {memo.memo.items.length === 1 ? "note" : "notes"}</GxBadge></p>
          <GxCroMemoList items={memo.memo.items.filter((i) => i.severity !== "info")} testPrefix="decide.compare" capped={team.draft?.capped} />
          {notes === 0 ? null : <p className="gx-small gx-muted" data-testid="decide.compare.notes">{notes === 1 ? "1 note is" : `${notes} notes are`} information only, shown once in the banner above the preview.</p>}
          {memo.memo.items.length === 0 ? <p className="gx-narr">Nothing to flag: the plan sits inside the limits.</p> : null}
        </div>
      )}
    </section>
  );
};
